import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trophy, Loader2, MapPin, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatCompDate(dateStr) {
    if (!dateStr) return dateStr;
    // Accepts either ISO "YYYY-MM-DD" or compact "YYYYMMDD"
    const digits = dateStr.replace(/-/g, '');
    if (digits.length < 8) return dateStr;
    const d = new Date(
        parseInt(digits.slice(0, 4), 10),
        parseInt(digits.slice(4, 6), 10) - 1,
        parseInt(digits.slice(6, 8), 10)
    );
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function FestivalJoinPage() {
    const { inviteCode } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, isLoading, festivalJoin } = useAuth();

    const [comp, setComp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checkingMembership, setCheckingMembership] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [company, setCompany] = useState('');
    // Guards the "already a member, skip the form" effect below from firing
    // off the back of our OWN handleSubmit flipping isAuthenticated (guest
    // sign-up) — without this it re-navigates to the same page but drops
    // the justSignedUp state, so the scoring rules popup never opens.
    const submittedRef = useRef(false);

    // Competition preview — public, no auth needed
    useEffect(() => {
        if (!inviteCode) return;
        axios.get(`${API_URL}/api/competitions/join/${inviteCode}`)
            .then(res => setComp(res.data.competition))
            .catch(() => toast.error('This link is not valid'))
            .finally(() => setLoading(false));
    }, [inviteCode]);

    // Already a member of THIS competition (returning guest, or a regular
    // RaceWise/PA account that's already been through this form) — skip
    // straight to the competition. Everyone else — including an
    // already-logged-in RaceWise account — still fills in the form, since
    // an existing login carries no company for this competition.
    useEffect(() => {
        if (isLoading || !isAuthenticated || !comp || submittedRef.current) return;
        let cancelled = false;
        setCheckingMembership(true);
        axios.get(`${API_URL}/api/competitions/${comp.id}/leaderboard`, {
            headers: { Authorization: `Bearer ${getToken()}` }
        }).then(res => {
            if (cancelled) return;
            if (res.data.user_entry) {
                navigate(`/competitions/${comp.id}`, { replace: true });
            } else {
                setCheckingMembership(false);
            }
        }).catch(() => {
            if (!cancelled) setCheckingMembership(false);
        });
        return () => { cancelled = true; };
    }, [isLoading, isAuthenticated, comp, navigate]);

    async function handleSubmit(e) {
        e.preventDefault();
        if (!firstName.trim() || !lastName.trim() || !company.trim()) {
            toast.error('Please fill in all three fields');
            return;
        }
        setSubmitting(true);
        submittedRef.current = true;
        try {
            if (isAuthenticated) {
                await axios.post(
                    `${API_URL}/api/festival/join/${inviteCode}`,
                    { first_name: firstName.trim(), last_name: lastName.trim(), company: company.trim() },
                    { headers: { Authorization: `Bearer ${getToken()}` } }
                );
                navigate(`/competitions/${comp.id}`, { replace: true, state: { justSignedUp: true } });
            } else {
                const userData = await festivalJoin(inviteCode, {
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    company: company.trim(),
                });
                navigate(`/competitions/${userData.restricted_comp_id}`, {
                    replace: true,
                    state: { justSignedUp: true },
                });
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Something went wrong — please try again');
            submittedRef.current = false;
        } finally {
            setSubmitting(false);
        }
    }

    if (loading || isLoading || checkingMembership) {
        return (
            <div className="container-app py-8 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!comp) {
        return (
            <div className="container-app py-8 text-center">
                <p className="text-muted-foreground">This link is not valid.</p>
            </div>
        );
    }

    return (
        <div className="container-app py-8">
            <div className="max-w-md mx-auto">
                <Card className="border-primary/20 bg-gradient-to-b from-primary/5 to-transparent">
                    <CardHeader className="text-center pb-4">
                        <div className="mx-auto w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                            <Trophy className="h-8 w-8 text-primary" />
                        </div>
                        <CardTitle className="text-2xl font-heading">{comp.name?.trim()}</CardTitle>
                        {comp.description && (
                            <CardDescription className="text-base">{comp.description.trim()}</CardDescription>
                        )}
                        <div className="flex items-center justify-center gap-4 pt-3 text-sm text-muted-foreground">
                            {comp.meeting_name && (
                                <span className="flex items-center gap-1">
                                    <MapPin className="h-3.5 w-3.5" />
                                    {comp.meeting_name}
                                </span>
                            )}
                            {comp.start_date && (
                                <span className="flex items-center gap-1">
                                    <Calendar className="h-3.5 w-3.5" />
                                    {formatCompDate(comp.start_date)}
                                </span>
                            )}
                        </div>
                    </CardHeader>
                    <form onSubmit={handleSubmit}>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="firstName">First name</Label>
                                <Input
                                    id="firstName"
                                    value={firstName}
                                    onChange={e => setFirstName(e.target.value)}
                                    autoComplete="given-name"
                                    autoFocus
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="lastName">Last name</Label>
                                <Input
                                    id="lastName"
                                    value={lastName}
                                    onChange={e => setLastName(e.target.value)}
                                    autoComplete="family-name"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="company">Company</Label>
                                <Input
                                    id="company"
                                    value={company}
                                    onChange={e => setCompany(e.target.value)}
                                    autoComplete="organization"
                                    required
                                />
                            </div>
                        </CardContent>
                        <CardFooter>
                            <Button type="submit" className="w-full btn-pill" size="lg" disabled={submitting}>
                                {submitting
                                    ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Joining...</>
                                    : 'Enter the Competition'}
                            </Button>
                        </CardFooter>
                    </form>
                </Card>
            </div>
        </div>
    );
}
